import {
  isIntradayResample,
  PriceHover,
  RangeHorizon,
  ResampleIntervals,
  SeriesType,
  TimeSeriesItem,
} from '@chart';
import { formatPriceLabel } from '@chart/utils/format';
import {
  ChartV2,
  ChartV2Handle,
  ChartV2Horizon,
  ChartV2HoverPayload,
  ChartV2Insight,
  ChartV2PriceDisplay,
  ChartV2Series,
  ChartV2SeriesType,
} from '@chartv2';
import {
  flip,
  offset,
  shift,
  useClientPoint,
  useFloating,
} from '@floating-ui/react';
import {
  getChartV2SeriesId,
  toChartV2Horizon,
  toChartV2Interval,
  toRangeHorizon,
} from '@terminal/views/chart-v2/utils/map-terminal-chart';
import { UseTurboChartReturn } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import type {
  TerminalChartAssetData,
  TerminalPaneData,
} from '@terminal/views/turbo-chart/utils/chart-utils/types';
import { bisector } from 'd3';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { SimpleChartTooltip } from '../simple-chart-tooltip/SimpleChartTooltip';

const CHARTV2_TO_SERIES_TYPE: Record<ChartV2SeriesType, SeriesType> = {
  line: 'line',
  area: 'line',
  candlestick: 'candlestick',
  bar: 'bars',
};

const bisectTsTime = bisector<TimeSeriesItem, number>(point =>
  new Date(point.time).getTime()
).left;

export interface ChartAreaV2Props {
  series: ChartV2Series[];
  chartData: TerminalChartAssetData[];
  primaryPane: TerminalPaneData;
  horizon: RangeHorizon | null;
  resample: ResampleIntervals;
  availableHorizons: ChartV2Horizon[];
  disable1DHorizon: boolean;
  insights?: ChartV2Insight[];
  enableZoom?: boolean;
  enableHover?: boolean;
  isLegacyVariant?: boolean;
  viewControllerClassName?: string;
  topBarActions?: ReactNode;
  onHorizonChange: (horizon: RangeHorizon) => void;
  changeSeriesType: UseTurboChartReturn['changeSeriesType'];
  domain?: { start: number; end: number } | null;
  defaultPriceDisplayByPane?: Record<string, ChartV2PriceDisplay>;
}

export const ChartAreaV2 = ({
  series,
  chartData,
  primaryPane,
  horizon,
  resample,
  availableHorizons,
  disable1DHorizon,
  insights,
  enableZoom = false,
  enableHover = true,
  isLegacyVariant = true,
  viewControllerClassName,
  topBarActions,
  onHorizonChange,
  changeSeriesType,
  domain,
  defaultPriceDisplayByPane,
}: ChartAreaV2Props) => {
  const [chartV2Hover, setChartV2Hover] = useState<ChartV2HoverPayload | null>(
    null
  );
  const chartV2Ref = useRef<ChartV2Handle>(null);
  const appliedDomainKeyRef = useRef<string | null>(null);

  // Apply a caller-supplied custom domain (e.g. Alfred charts / ChartMessage
  // pass an explicit date range with no horizon) imperatively — ChartV2 has
  // no controlled `domain` prop, so without this it falls back to its
  // default horizon and the visible range drifts off the requested window.
  useEffect(() => {
    if (!domain || series.length === 0) {
      appliedDomainKeyRef.current = null;
      return;
    }

    const key = `${domain.start}:${domain.end}`;

    if (appliedDomainKeyRef.current === key || !chartV2Ref.current) {
      return;
    }

    chartV2Ref.current.setDomain(domain);
    appliedDomainKeyRef.current = key;
  }, [domain, series.length]);

  const chartDataBySeriesId = useMemo(
    () => new Map(chartData.map(asset => [getChartV2SeriesId(asset), asset])),
    [chartData]
  );

  const chartV2PriceHover = useMemo<PriceHover | undefined>(() => {
    if (!chartV2Hover) {
      return undefined;
    }

    const priceDisplay =
      chartV2Hover.priceDisplay === 'indexed' ? 'percentage' : 'price';

    const data = chartV2Hover.hits
      .map(hit => {
        const chartAssetData = chartDataBySeriesId.get(hit.seriesId);

        if (!chartAssetData) {
          return null;
        }

        const currentIndex = bisectTsTime(chartAssetData.ts, hit.time);
        const currentPrice = chartAssetData.ts[currentIndex];

        if (!currentPrice) {
          return null;
        }

        return {
          chartAssetData,
          currentPrice,
          previousPrice:
            currentIndex > 0
              ? chartAssetData.ts[currentIndex - 1]
              : currentPrice,
          priceFormatted: formatPriceLabel(hit.value, priceDisplay),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    if (data.length === 0) {
      return undefined;
    }

    return {
      panes: [{ data, id: primaryPane.id }],
      date: new Date(chartV2Hover.time),
      x: chartV2Hover.clientX,
      y: chartV2Hover.clientY,
    };
  }, [chartV2Hover, chartDataBySeriesId, primaryPane]);

  const { refs, floatingStyles, context } = useFloating({
    open: !!chartV2PriceHover,
    placement: 'top',
    strategy: 'fixed',
    middleware: [offset(15), flip(), shift({ padding: 5 })],
  });
  useClientPoint(context, {
    x: chartV2PriceHover?.x,
    y: chartV2PriceHover?.y,
  });

  return (
    <>
      <div ref={refs.setReference}>
        <ChartV2
          ref={chartV2Ref}
          viewControllerClassName={viewControllerClassName}
          series={series}
          horizon={toChartV2Horizon(horizon)}
          interval={toChartV2Interval(resample)}
          availableHorizons={availableHorizons}
          disabledHorizons={disable1DHorizon ? ['1D'] : undefined}
          autoDisableHorizonsByData={false}
          enableZoom={enableZoom}
          hoverVariant="simple"
          showIntervalSelector={false}
          showViewController={!isLegacyVariant}
          showDefaultLegend={false}
          topBarActions={topBarActions}
          insights={insights}
          onHoverChange={enableHover ? setChartV2Hover : undefined}
          onHorizonChange={newHorizon =>
            onHorizonChange(toRangeHorizon(newHorizon))
          }
          onSeriesTypeChange={(_, type) =>
            changeSeriesType(CHARTV2_TO_SERIES_TYPE[type], {})
          }
          defaultPriceDisplayByPane={defaultPriceDisplayByPane}
        />
      </div>
      {context.open && chartV2PriceHover && (
        <SimpleChartTooltip
          ref={refs.setFloating}
          styles={floatingStyles}
          priceHover={chartV2PriceHover}
          shouldUseLocalDate={isIntradayResample(resample)}
        />
      )}
    </>
  );
};
